import { useState } from "react";
import { useTranslation } from "react-i18next";
import truncate from "lodash/truncate";
import { Box } from "@mui/material";
import { useAccountsId } from "@rentablo/api/apiv1";
import { CardPageLayout } from "@rentablo/components";
import { BitpandaV1Banner } from "@components";
import { useCurrencyDisplay } from "@providers/CurrencyDisplayProvider";
import { useRangeSelectControl } from "@hooks";
import { ChartControlsProvider } from "@components/Chart/ChartControls";
import { AccountProtocolTable } from "./components/AccountProtocolTable";
import { SingleAccountPageChart } from "./components/SingleAccountPageChart";
import { SingleAccountPageHeading } from "./components/SingleAccountPageHeading";

interface Props {
  accountId: number;
}

export const SingleAccountPage = ({ accountId }: Props) => {
  const { t } = useTranslation();
  const transactionsRangeProps = useRangeSelectControl({
    firstDate: new Date(),
  });
  const [protocolFilter, setProtocolFilter] = useState("");

  const { selectedCurrencyView, getDisplayCurrency } = useCurrencyDisplay();

  const { data: account } = useAccountsId({
    id: accountId,
  });

  const isOriginal = selectedCurrencyView === "original";
  const titleCurrency = getDisplayCurrency(account?.currency);
  const titleBalance = isOriginal
    ? (account?.balance ?? 0)
    : (account?.balanceInDisplayCurrency ?? 0);

  const breadcrumbs = [
    { to: "/", text: t("breadcrumbs.home") },
    {
      to: "/depots-accounts",
      text: t("breadcrumbs.depotsAccounts"),
    },
    { text: truncate(account?.name) },
  ];

  return (
    <CardPageLayout
      breadcrumbs={breadcrumbs}
      banner={<BitpandaV1Banner account={account} />}
      cardHeading={
        <SingleAccountPageHeading accountId={accountId} account={account} />
      }
      cardChildren={
        <ChartControlsProvider rangeId="account">
          <Box>
            <SingleAccountPageChart
              accountId={accountId}
              balance={titleBalance}
              currency={titleCurrency}
            />
          </Box>
        </ChartControlsProvider>
      }
    >
      <AccountProtocolTable
        summaryValue={titleBalance}
        accountId={accountId}
        currency={titleCurrency}
        rangeProps={transactionsRangeProps}
        filter={protocolFilter}
        onFilterChange={setProtocolFilter}
      />
    </CardPageLayout>
  );
};
